/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import './app.common';

import _ from 'lodash';
import Backbone from 'backbone';
import Cookie from 'js-cookie';
import Radio from 'backbone.radio';
import NProgress from 'nprogress';
import sha1 from 'psa-core/util/sha1';

import Session from '@/Session';
import Scaffold from '@/Scaffold';
import Config from '@config/Config';
import GetExternalUserDetails from '@/services/GetExternalUserDetails';
import deleteOTDSSession from '@/services/DeleteOTDSSessions';
import clearAuthenticationCookies from '@/services/ClearAuthenticationCookies';
import clearExternalUserCookies from '@/services/ClearExternalUserCookies';
import clearAllCookies from '@/services/ClearAllCookies';
import InactivityTimer from '@/util/InactivityTimer';
import * as NavUtils from '@/util/NavUtils';

const samlArtifactCookieName = Config.getSamlArtifactCookieName();
const checkCookieName = Config.getCheckCookieName();
const externalSessionCookieName = Config.getExternalSessionCookieName();

handleEntry();

async function handleEntry() {
  const sessionCookieVal = Cookie.get(externalSessionCookieName);
  const samlArtifact = Cookie.get(samlArtifactCookieName);
  const checkCookieVal = Cookie.get(checkCookieName);

  if (!sessionCookieVal) {
    Radio.channel('navigate').request('show:ext:login');
  } else if (sessionCookieVal && samlArtifact && checkCookieVal) {
    signIn();
  } else if (sessionCookieVal && samlArtifact) {
    setSessionAuth(samlArtifact);
    signIn();
  } else {
    finishLogout();
  }
}

function setSessionAuth(samlArtifact) {
  const cookiesPath = Config.getSamlArtifactCookiePath();
  const ct = sha1.hex_sha1(samlArtifact);
  Cookie.set(checkCookieName, ct, { path: cookiesPath });
  Session.auth = { samlArtifact, checkValue: ct };
}

async function signIn() {
  try {
    const userProfile = await GetExternalUserDetails();
    Session.user = userProfile;
    finishLogin();
  } catch (err) {
    Radio.channel('navigate').request('show:ext:login');
    throw err;
  }
}

function setupInactivityTimer() {
  const inactivityMinutes = 60;
  const warningMinutes = 10;
  const inactivityMilliseconds = inactivityMinutes * 60 * 1000;
  const warningMilliseconds = warningMinutes * 60 * 1000;

  let warningMessage;

  const timer = new InactivityTimer({
    inactivityMilliseconds,
    warningMilliseconds,
    onInactivity() {
      finishLogout();
      if (warningMessage && _.isFunction(warningMessage.hide)) {
        warningMessage.hide();
      }
      Messenger().post({
        message: 'You have been logged out due to inactivity.',
        hideAfter: false,
        showCloseButton: true
      });
    },
    onWarning() {
      warningMessage = Messenger().post({
        message: `You will be automatically logged out if no action is taken during the next ${warningMinutes} minutes.`,
        showCloseButton: true,
        hideAfter: false,
        actions: {
          active: {
            label: 'Keep Session Active',
            action() {
              warningMessage.hide();
              timer.restart();
            }
          }
        }
      });
    },
    onTimerReset() {
      if (warningMessage && _.isFunction(warningMessage.hide)) {
        warningMessage.hide();
      }
    }
  });
  return timer;
}

function finishLogin() {
  Scaffold.getRegion('content').empty();
  Messenger().hideAll();
  if (Backbone.history) {
    if (!Backbone.History.started) {
      Backbone.history.start();
    }
    Radio.channel('header').trigger('login:external');
    Radio.channel('header').trigger('show:external');
    Radio.channel('footer').trigger('show:external');
    if (NavUtils.getCurrentRoute() === '') {
      Radio.channel('navigate').request(Config.getDefaultNavigationTrigger());
    }
  }
  window.inactivityTimer = setupInactivityTimer();
  window.inactivityTimer.start();
}

async function finishLogout() {
  NProgress.start();
  NProgress.set(0.5);

  // Log out of OTDS
  const otdsTicketCookieName = Config.getOTDSTicketCookieName();
  const otdsTicket = Cookie.get(otdsTicketCookieName);
  if (otdsTicket) await deleteOTDSSession(otdsTicket);

  // Destroy authentication cookies
  await clearAuthenticationCookies();

  // Destroy Session cookies
  await clearExternalUserCookies();

  // In case all cookies aren't caught, delete the rest:
  clearAllCookies();

  // Empty all session values;
  Session.auth = undefined;
  Session.user = undefined;
  Session.config = undefined;

  // Stop inactivity timer.
  window.inactivityTimer?.stop();

  Backbone.history?.stop();
  Radio.channel('header').trigger('hide:header');
  Radio.channel('footer').trigger('hide:footer');
  Radio.channel('navigate').request('show:ext:login');

  // Navigate to login page
  NavUtils.navigate('');
  NProgress.done();
}

/**
 * Handle the completion of the authentication, regardless of source. Assumption
 * at this point is that Session.(etc) data is populated
 */
Radio.channel('global').on('login:finish', () => {
  finishLogin();
});

Radio.channel('global').on('logout:finish', () => {
  finishLogout();
});
